<script setup lang="ts">
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useAgenticSearchStore } from '@/search/agentic_search.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import { vFocusOnMount } from '@/util/focus_on_mount'

const FAILED_SEARCH_MESSAGE = 'We\'re not able to finish retrieving assessments. Try again later.'

const props = defineProps<{ shownCount: number }>()

const analytics = getAnalyticsTracker()
const agenticSearch = useAgenticSearchStore()
const { assessmentResults, latestSearch, loading: isLoading, error } = storeToRefs(agenticSearch)

const hasSearch = computed(() => Boolean(latestSearch.value.prompt))
const totalCount = computed(() => assessmentResults.value.length)
// Only a non-premium user is shown fewer results than the wizard found.
const countHiddenWithoutPremium = computed(() => totalCount.value - props.shownCount)
const hasFailed = computed(() => Boolean(error.value))
const hasFinishedWithResults = computed(() => !isLoading.value && totalCount.value > 0)
const hasFinishedWithoutResults = computed(() => !isLoading.value && totalCount.value === 0 && !hasFailed.value)

function averageRelevanceScore(): number {
  const totalScore = assessmentResults.value.reduce((sum, result) => sum + (result.relevance_score ?? 0), 0)
  return totalScore / totalCount.value
}

function completedStreamEventData() {
  const eventData = {
    assessment_wizard_num_results: totalCount.value,
    search_query: latestSearch.value.prompt,
    selected_grade: latestSearch.value.searchGrade,
    agentic_search_id: latestSearch.value.searchId,
    input_tokens: agenticSearch.latestTokenUsage.inputTokens,
    output_tokens: agenticSearch.latestTokenUsage.outputTokens
  }
  if (!totalCount.value) {
    return eventData
  }
  return { ...eventData, average_relevance_score: averageRelevanceScore() }
}

watch(isLoading, (isNowLoading, wasLoading) => {
  const hasJustFinished = wasLoading && !isNowLoading
  if (!hasJustFinished || !hasSearch.value) {
    return
  }
  analytics.sendEvent(EVENT_NAMES.BROWSE_ASSESSMENTS.COMPLETED_WIZARD_RESULTS_STREAM, completedStreamEventData())
})
</script>

<template>
  <template v-if="hasSearch">
    <div v-focus-on-mount tabindex="-1" class="mb-4 flex items-center whitespace-pre pb-1.5 pt-0.5 text-lg leading-6 text-gray-700 focus:outline-none" data-testid="wizard-results-count">
      <span v-if="isLoading" class="material-icons-round mr-1 animate-spin text-[1em]" aria-hidden="true">autorenew</span>
      <span class="text-xl font-bold">{{ shownCount }}</span><span> results found</span>
      <span v-if="countHiddenWithoutPremium > 0"> ({{ countHiddenWithoutPremium }} more with premium)</span>
    </div>
    <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
      <span v-if="isLoading">Loading Assessment Wizard results</span>
      <span v-else-if="hasFailed">{{ FAILED_SEARCH_MESSAGE }}</span>
      <span v-else-if="hasFinishedWithResults">
        {{ shownCount }} results found. Loading complete.
        <template v-if="countHiddenWithoutPremium > 0">{{ countHiddenWithoutPremium }} more results available with premium.</template>
      </span>
      <span v-else-if="hasFinishedWithoutResults">No results found. Loading complete.</span>
    </div>
    <div v-if="hasFailed" class="mb-4 flex items-center gap-2 rounded-xs bg-[#f6e1e0] p-2">
      <span class="material-icons-round flex-shrink-0 text-display-sm text-gray-900" aria-hidden="true">warning_amber</span>
      <p class="text-md leading-5 text-gray-900" data-testid="wizard-search-failed">{{ FAILED_SEARCH_MESSAGE }}</p>
    </div>
    <p v-if="hasFinishedWithoutResults" class="mb-4 border border-blue-300 bg-blue-200 py-2.5 pl-4 pr-9 text-md text-blue-500">
      Sorry, we currently don't have any assessments matching your query. Try returning to the Assessment Wizard to modify your request.
    </p>
  </template>
</template>
